import React, { useState, useEffect, useMemo } from 'react';
import {
  IoSearch,
  IoAdd,
  IoPaperPlane,
  IoAttach,
  IoCheckmark,
  IoChatbubbles,
  IoArrowForward,
  IoFileTray,
  IoConstruct,
  IoChevronBack,
  IoChevronForward,
} from 'react-icons/io5';
import { User } from '@/types';
import './Messages.css';

interface MessagesProps {
  user?: User | null;
}

interface Conversation {
  id: string;
  name: string;
  role: string;
  lastMessage: string;
  timeAgo: string;
  unreadCount?: number;
  isOnline?: boolean;
  avatar?: string;
  avatarType?: 'image' | 'gradient';
  gradientColors?: string;
}

const Messages: React.FC<MessagesProps> = ({ user }) => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread'>('all');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [isConversationListCollapsed, setIsConversationListCollapsed] = useState(false);

  const conversations: Conversation[] = useMemo(() => [
    {
      id: '1',
      name: 'Mr. David Thompson',
      role: 'School Counselor',
      lastMessage: 'I reviewed your Stanford essay and have some suggestions for improvement...',
      timeAgo: '2h ago',
      unreadCount: 2,
      isOnline: true,
      avatar: '/api/placeholder/40/40',
      avatarType: 'image',
    },
    {
      id: '2',
      name: 'AI Assistant',
      role: 'College Application Help',
      lastMessage: 'Here are some colleges that match your profile and preferences...',
      timeAgo: '1d ago',
      avatarType: 'gradient',
      gradientColors: 'from-blue-500 to-purple-600',
    },
    {
      id: '3',
      name: 'AI Assistant',
      role: 'Essay Review Query',
      lastMessage: 'Your essay demonstrates strong narrative voice and personal growth...',
      timeAgo: '3d ago',
      avatarType: 'gradient',
      gradientColors: 'from-green-500 to-teal-600',
    },
    {
      id: '4',
      name: 'AI Assistant',
      role: 'Application Timeline',
      lastMessage: "Based on your deadlines, here's a suggested timeline for completion...",
      timeAgo: '5d ago',
      avatarType: 'gradient',
      gradientColors: 'from-orange-500 to-red-600',
    },
    {
      id: '5',
      name: 'Ms. Jennifer Lee',
      role: 'College Advisor',
      lastMessage: 'Great job on your MIT application! Just a few minor adjustments needed...',
      timeAgo: '1w ago',
      avatar: '/api/placeholder/40/40',
      avatarType: 'image',
    },
  ], []);

  const filteredConversations = useMemo(() => {
    if (selectedFilter === 'unread') {
      return conversations.filter(c => c.unreadCount && c.unreadCount > 0);
    }
    return conversations;
  }, [conversations, selectedFilter]);

  const handleSendMessage = () => {
    if (messageText.trim()) {
      console.log('Sending message:', messageText);
      setMessageText('');
    }
  };

  const handleQuickSuggestion = (suggestion: string) => {
    setMessageText(suggestion);
  };

  const handleNewConversation = () => {
    console.log('Starting new conversation');
    setSelectedConversation(null);
  };

  const getGradientStyle = (gradientColors: string) => {
    switch (gradientColors) {
      case 'from-blue-500 to-purple-600':
        return 'var(--gradient-blue-purple)';
      case 'from-green-500 to-teal-600':
        return 'var(--gradient-green-teal)';
      case 'from-orange-500 to-red-600':
        return 'var(--gradient-orange-red)';
      default:
        return 'var(--gradient-blue-purple)';
    }
  };

  return (
    <div className="messages-page">
      <header className="sticky top-0 z-50 w-full border-b flex items-center justify-between px-8 py-4 shadow-sm" style={{ background: 'var(--surface-secondary)', borderColor: 'var(--border-primary)' }}>
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Showing {filteredConversations.length} of {conversations.length} conversations
        </span>
      </header>

      {/* Messaging Interface */}
      <div className="messaging-interface">
        {/* Conversation List */}
        <div className={`conversation-list ${isConversationListCollapsed ? 'collapsed' : ''}`}>
          <div className="conversation-list-header">
            {!isConversationListCollapsed && (
              <>
                <h2 className="conversation-list-title">Conversations</h2>
                <div className="search-container">
                  <IoSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="search-input"
                  />
                </div>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn ${selectedFilter === 'unread' ? 'active' : ''}`}
                    onClick={() => setSelectedFilter('unread')}
                  >
                    Unread
                  </button>
                </div>
              </>
            )}
            <button
              className="collapse-toggle-btn"
              onClick={() => setIsConversationListCollapsed(!isConversationListCollapsed)}
              title={isConversationListCollapsed ? 'Expand' : 'Collapse'}
            >
              {isConversationListCollapsed ? <IoChevronForward /> : <IoChevronBack />}
            </button>
          </div>

          <div className="conversations-container">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`conversation-item ${selectedConversation === conversation.id ? 'selected' : ''}`}
                onClick={() => setSelectedConversation(conversation.id)}
              >
                <div className="conversation-content">
                  <div className="avatar-container">
                    {conversation.avatarType === 'image' && conversation.avatar ? (
                      <>
                        <img
                          src={conversation.avatar}
                          alt={conversation.name}
                          className="avatar-image"
                        />
                        {conversation.isOnline && <span className="online-indicator" />}
                      </>
                    ) : (
                      <div
                        className="avatar-gradient"
                        style={{
                          background: getGradientStyle(conversation.gradientColors || ''),
                        }}
                      >
                        <IoConstruct className="avatar-icon" />
                      </div>
                    )}
                  </div>
                  {!isConversationListCollapsed && (
                    <div className="conversation-details">
                      <div className="conversation-header">
                        <h3 className="conversation-name">{conversation.name}</h3>
                        <span className="conversation-time">{conversation.timeAgo}</span>
                      </div>
                      <p className="conversation-role">{conversation.role}</p>
                      <p className="conversation-preview">{conversation.lastMessage}</p>
                      {conversation.unreadCount && (
                        <div className="unread-badge-container">
                          <span className="unread-badge">{conversation.unreadCount}</span>
                          <span className="unread-text">Unread</span>
                        </div>
                      )}
                    </div>
                  )}
                  {isConversationListCollapsed && conversation.unreadCount && (
                    <span className="unread-badge-collapsed">{conversation.unreadCount}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="new-conversation-section">
            <button 
              className={`new-conversation-btn ${isConversationListCollapsed ? 'collapsed' : ''}`}
              onClick={handleNewConversation}
              title={isConversationListCollapsed ? 'New Conversation' : ''}
            >
              <IoAdd />
              {!isConversationListCollapsed && <span>New Conversation</span>}
            </button>
          </div>
        </div>

        {/* Main Messaging Area */}
        <div className="main-messaging-area">
          {!selectedConversation ? (
            <div className="empty-state">
              <div className="empty-state-content-wrapper">
                <div className="empty-state-content">
                  <div className="empty-state-icon-container">
                    <IoChatbubbles className="empty-state-icon" />
                  </div>
                  <h2 className="empty-state-title">Ask us anything</h2>
                  <p className="empty-state-description">
                    We'll try our best to answer your question and if we can't we'll help pass on your question to your counselor!
                  </p>

                  <div className="features-card">
                    <div className="features-list">
                      <div className="feature-item">
                        <div className="feature-check">
                          <IoCheckmark className="check-icon" />
                        </div>
                        <p>Find what you're looking for in the app</p>
                      </div>
                      <div className="feature-item">
                        <div className="feature-check">
                          <IoCheckmark className="check-icon" />
                        </div>
                        <p>Answer questions about colleges</p>
                      </div>
                      <div className="feature-item">
                        <div className="feature-check">
                          <IoCheckmark className="check-icon" />
                        </div>
                        <p>Plan your next moves</p>
                      </div>
                    </div>

                    <button className="counselor-button">
                      <img
                        src="/api/placeholder/20/20"
                        alt="Counselor"
                        className="counselor-avatar"
                      />
                      <span>Message my counselor directly</span>
                      <IoArrowForward className="arrow-icon" />
                    </button>

                    <p className="ai-hint">Or start typing below to chat with our AI assistant</p>
                  </div>

                  {/* Quick Suggestions */}
                  <div className="quick-suggestions">
                    <p className="suggestions-title">Quick suggestions:</p>
                    <div className="suggestions-grid">
                      {[
                        "What's my application status?",
                        'Help with my essay',
                        'College recommendations',
                        'Upcoming deadlines',
                      ].map((suggestion) => (
                        <button
                          key={suggestion}
                          className="suggestion-button"
                          onClick={() => handleQuickSuggestion(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Message Input Area - Always visible at bottom */}
              <div className="message-input-area">
                <div className="input-container">
                  <button className="attach-button">
                    <IoAttach className="attach-icon" />
                  </button>
                  <textarea
                    rows={1}
                    placeholder="Type your message here..."
                    className="message-textarea"
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 80)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="send-button"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                  >
                    <IoPaperPlane className="send-icon" />
                  </button>
                </div>
                <div className="input-footer">
                  <button className="attach-file-link">
                    <IoFileTray className="file-icon" />
                    <span>Attach file</span>
                  </button>
                  <p className="input-hint">Press Enter to send</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="active-conversation">
              <div className="conversation-header-bar">
                <h3 className="active-conversation-name">
                  {conversations.find(c => c.id === selectedConversation)?.name}
                </h3>
              </div>
              <div className="messages-container">
                <p className="no-messages">Select a conversation to view messages</p>
              </div>
              <div className="message-input-area">
                <div className="input-container">
                  <button className="attach-button">
                    <IoAttach className="attach-icon" />
                  </button>
                  <textarea
                    rows={1}
                    placeholder="Type your message here..."
                    className="message-textarea"
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="send-button"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                  >
                    <IoPaperPlane className="send-icon" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;